import type { DrawingDocument, DrawingLine, GraphEdge, GraphNode, NormalizedPoint } from '../model';
import {
  nextSequentialId,
  nodeAtPoint,
  replaceTopology,
  roundCoordinate,
  samePoint,
  withoutKey
} from './drawingDocumentCore.ts';
import { reconcileValenceCharges } from './valenceCharge.ts';

const DEFAULT_ATOM = { atomicNumber: 6 } as const;
const DEFAULT_BOND = { order: 1, type: 'covalent' } as const;
const DEFAULT_FORMAL_CHARGE = 0;

export function deleteBond(document: DrawingDocument, edgeId: string): DrawingDocument {
  if (!document.structure.bonds[edgeId]) return document;
  const graph = {
    ...document.structure.graph,
    revision: document.structure.graph.revision + 1,
    edges: document.structure.graph.edges.filter((edge) => edge.id !== edgeId)
  };
  return reconcileValenceCharges(
    replaceTopology(
      document,
      graph,
      document.structure.atoms,
      withoutKey(document.structure.bonds, edgeId),
      document.structure.formalCharges,
      document.depiction.coordinateSet.coordinates,
      document.depiction.atomLabels,
      withoutKey(document.depiction.bondLengthOverrides, edgeId)
    ),
    document
  );
}

export function deleteAtom(document: DrawingDocument, nodeId: string): DrawingDocument {
  if (!document.structure.atoms[nodeId]) return document;
  const removedEdgeIds = new Set(
    document.structure.graph.edges
      .filter((edge) => edge.source === nodeId || edge.target === nodeId)
      .map((edge) => edge.id)
  );
  const graph = {
    ...document.structure.graph,
    revision: document.structure.graph.revision + 1,
    nodes: document.structure.graph.nodes.filter((node) => node.id !== nodeId),
    edges: document.structure.graph.edges.filter((edge) => !removedEdgeIds.has(edge.id))
  };
  return reconcileValenceCharges(
    replaceTopology(
      document,
      graph,
      withoutKey(document.structure.atoms, nodeId),
      Object.fromEntries(Object.entries(document.structure.bonds).filter(([edgeId]) => !removedEdgeIds.has(edgeId))),
      withoutKey(document.structure.formalCharges, nodeId),
      withoutKey(document.depiction.coordinateSet.coordinates, nodeId),
      withoutKey(document.depiction.atomLabels, nodeId),
      Object.fromEntries(
        Object.entries(document.depiction.bondLengthOverrides).filter(([edgeId]) => !removedEdgeIds.has(edgeId))
      )
    ),
    document
  );
}

export function edgeAtLine(document: DrawingDocument, line: DrawingLine): GraphEdge | undefined {
  const source = nodeAtPoint(document, line.start);
  const target = nodeAtPoint(document, line.end);
  if (!source || !target) return undefined;
  return document.structure.graph.edges.find(
    (edge) =>
      (edge.source === source.id && edge.target === target.id) ||
      (edge.source === target.id && edge.target === source.id)
  );
}

export function appendLineToDocument(document: DrawingDocument, line: DrawingLine): DrawingDocument {
  const coordinateSet = document.depiction.coordinateSet;
  const nodes = [...document.structure.graph.nodes];
  const coordinates = { ...coordinateSet.coordinates };
  const atoms = { ...document.structure.atoms };
  const formalCharges = { ...document.structure.formalCharges };
  const resolveNode = (point: NormalizedPoint): GraphNode => {
    const existing =
      nodeAtPoint(document, point) ??
      nodes.find((node) => {
        const coordinate = coordinates[node.id];
        return (
          coordinate &&
          samePoint(point, {
            x: coordinate.x / coordinateSet.width,
            y: coordinate.y / coordinateSet.height
          })
        );
      });
    if (existing) return existing;
    const node = {
      id: nextSequentialId(
        nodes.map((candidate) => candidate.id),
        'n'
      )
    };
    nodes.push(node);
    atoms[node.id] = DEFAULT_ATOM;
    formalCharges[node.id] = DEFAULT_FORMAL_CHARGE;
    coordinates[node.id] = {
      x: roundCoordinate(point.x * coordinateSet.width),
      y: roundCoordinate(point.y * coordinateSet.height)
    };
    return node;
  };
  const source = resolveNode(line.start);
  const target = resolveNode(line.end);
  if (
    source.id === target.id ||
    document.structure.graph.edges.some(
      (edge) =>
        (edge.source === source.id && edge.target === target.id) ||
        (edge.source === target.id && edge.target === source.id)
    )
  )
    return document;
  const edge: GraphEdge = {
    id: nextSequentialId(
      document.structure.graph.edges.map((candidate) => candidate.id),
      'e'
    ),
    source: source.id,
    target: target.id
  };
  const graph = {
    ...document.structure.graph,
    revision: document.structure.graph.revision + 1,
    nodes,
    edges: [...document.structure.graph.edges, edge]
  };
  return reconcileValenceCharges(
    replaceTopology(
      document,
      graph,
      atoms,
      { ...document.structure.bonds, [edge.id]: DEFAULT_BOND },
      formalCharges,
      coordinates,
      document.depiction.atomLabels,
      document.depiction.bondLengthOverrides
    ),
    document
  );
}

// UI action rules (docs/ui-action-rules.md) Rule 4.4: with a periodic-table element armed, a
// click on empty drawingPaper places a lone atom of that element -- a disconnected node, i.e. a
// new molecule. `point` is normalized in the document's own coordinate space.
export function addAtom(document: DrawingDocument, point: NormalizedPoint, atomicNumber: number): DrawingDocument {
  if (!Number.isInteger(atomicNumber) || atomicNumber < 1 || atomicNumber > 118) return document;
  const coordinateSet = document.depiction.coordinateSet;
  const id = nextSequentialId(
    document.structure.graph.nodes.map((node) => node.id),
    'n'
  );
  const graph = {
    ...document.structure.graph,
    revision: document.structure.graph.revision + 1,
    nodes: [...document.structure.graph.nodes, { id }]
  };
  // No reconcileValenceCharges here: a freshly placed lone atom has no bonds, so it can't be
  // over-valent, and no existing atom's bonding changed.
  return replaceTopology(
    document,
    graph,
    { ...document.structure.atoms, [id]: { atomicNumber } },
    document.structure.bonds,
    { ...document.structure.formalCharges, [id]: DEFAULT_FORMAL_CHARGE },
    {
      ...coordinateSet.coordinates,
      [id]: {
        x: roundCoordinate(point.x * coordinateSet.width),
        y: roundCoordinate(point.y * coordinateSet.height)
      }
    },
    document.depiction.atomLabels,
    document.depiction.bondLengthOverrides
  );
}

export function removeLastEdge(document: DrawingDocument): DrawingDocument {
  if (document.structure.graph.edges.length === 0) return document;
  const edges = document.structure.graph.edges.slice(0, -1);
  const referencedNodes = new Set(edges.flatMap((edge) => [edge.source, edge.target]));
  const nodes = document.structure.graph.nodes.filter((node) => referencedNodes.has(node.id));
  const retainedEdgeIds = new Set(edges.map((edge) => edge.id));
  const graph = {
    ...document.structure.graph,
    revision: document.structure.graph.revision + 1,
    nodes,
    edges
  };
  return reconcileValenceCharges(
    replaceTopology(
      document,
      graph,
      Object.fromEntries(Object.entries(document.structure.atoms).filter(([nodeId]) => referencedNodes.has(nodeId))),
      Object.fromEntries(Object.entries(document.structure.bonds).filter(([edgeId]) => retainedEdgeIds.has(edgeId))),
      Object.fromEntries(
        Object.entries(document.structure.formalCharges).filter(([nodeId]) => referencedNodes.has(nodeId))
      ),
      Object.fromEntries(
        Object.entries(document.depiction.coordinateSet.coordinates).filter(([nodeId]) => referencedNodes.has(nodeId))
      ),
      Object.fromEntries(
        Object.entries(document.depiction.atomLabels).filter(([nodeId]) => referencedNodes.has(nodeId))
      ),
      Object.fromEntries(
        Object.entries(document.depiction.bondLengthOverrides).filter(([edgeId]) => retainedEdgeIds.has(edgeId))
      )
    ),
    document
  );
}
